/**
 * Copyright (c) 2025-2026 Four Hour Window LLC
 * All rights reserved.
 * 
 * This source code is proprietary and confidential.
 * Unauthorized copying, distribution, or use is strictly prohibited.
 */

import './App.css';
import { CurrentData, AppContentProps } from './types';
import LoginComponent from "./components/Login/LoginComponent.tsx";
import ShopifySalesDashboard from "./components/ShopifySalesDashboard.tsx";
import CreateAccount from "./components/CreateAccount.tsx";
import EmailVerification from "./components/EmailVerification.tsx";
import MyAccount from './components/MyAccount.tsx';
import MyProducts from './components/MyProducts.tsx';
import EditContent from './components/EditContent/EditContent.tsx';
import AdminTools from './components/Admin/AdminTools.tsx';
import KnowledgeCenter from './components/KnowledgeCenter.tsx';
import ResetPassword from './components/ResetPassword.tsx';
import GlobalCollectionSelector from './components/GlobalCollectionSelector.tsx';
import DashboardHeroBanner from './components/DashboardHeroBanner.tsx';
import ConstructionScreen from './components/ConstructionScreen.tsx';
import { SystemConfig, UnderConstruction } from './types/index.ts';
import { useState, useRef, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { onAuthStateChanged, signOut } from 'firebase/auth';
import { doc, getDoc, onSnapshot } from 'firebase/firestore';
import { auth, db } from './firebase.js';
import { ProductsCacheProvider } from './contexts/ProductsCacheContext.tsx';
import { SalesDataProvider } from './contexts/SalesDataContext.tsx';
import { CollectionProvider } from './contexts/CollectionContext.tsx';
import { usePushNotifications, TappedOrderData } from './hooks/usePushNotifications.ts';
import OrderSoldModal from './components/OrderSoldModal.tsx';
import { Capacitor } from '@capacitor/core';
import { SplashScreen } from '@capacitor/splash-screen';
import { Badge } from '@capawesome/capacitor-badge';
import { App as CapacitorApp } from '@capacitor/app';
import splashLogo from './images/splash-logo.png';
import { API_BASE_URL } from './utils/apiBaseUrl.ts';
import { pushPermissionSettled } from './utils/pushPermissionGate.ts';


// Clears the "items sold since last opened" badge - both the native icon
// badge (instant, no network wait) and the server-side counter it's driven
// from, so the next order starts counting from 0 instead of compounding on
// a stale total. Shared by the effects below: one for the initial
// login/cold-launch-with-restored-session case, one for every later resume
// via @capacitor/app, and a visibilitychange fallback that doesn't depend
// on that plugin at all.
//
// Logs every step (not just failures) - two rounds of fixes here still
// didn't resolve the report that the badge doesn't clear, so the next
// debugging pass needs to be from real device logs (Safari's Web
// Inspector, Develop menu > [device] > the app, while it's running on the
// phone) rather than another guess from reading the code.
function clearUnseenSoldBadge(trigger: string) {
  console.log(`🔖[${trigger}] clearUnseenSoldBadge() called`);

  // Waits for usePushNotifications' broader permission request to have
  // been made first - see pushPermissionGate.ts. Every Badge plugin method
  // (including clear()) internally makes its own narrower .badge-only
  // authorization request as a side effect; calling it before Firebase
  // Messaging's alert+badge+sound request runs risks that narrower one
  // winning the race to be the device's first-ever permission dialog,
  // silently capping what the user is ever asked to grant - which is
  // exactly what caused sound to stop working after this badge feature
  // was added, even though the badge itself kept working fine throughout.
  pushPermissionSettled.then(() => {
    Badge.clear()
      .then(() => console.log(`🔖[${trigger}] Badge.clear() succeeded`))
      .catch((err) => console.warn(`🔖[${trigger}] Badge.clear() failed:`, err));
  });

  const user = auth.currentUser;
  if (!user) {
    console.warn(`🔖[${trigger}] auth.currentUser is null - skipping server reset`);
    return;
  }

  user.getIdToken()
    .then((idToken) => {
      console.log(`🔖[${trigger}] got ID token, calling reset-unseen-sold-count...`);
      return fetch(`${API_BASE_URL}/.netlify/functions/reset-unseen-sold-count`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${idToken}` },
      });
    })
    .then((response) => {
      console.log(`🔖[${trigger}] reset-unseen-sold-count responded: ${response.status}`);
    })
    .catch((err) => {
      console.warn(`🔖[${trigger}] reset-unseen-sold-count failed:`, err);
    });
}

// Wrapper component to conditionally show navigation
function AppContent({ currentData, firebaseToken, handleOnDataSend, handleLogout, dashboardRef }: AppContentProps) {
  // Diagnostic: useState's lazy initializer only runs once per mount, so a
  // changing mountId across renders proves AppContent is fully
  // unmounting/remounting (which would also explain navigate changing
  // identity and the push-notification effect re-running with a stale
  // userId that still reads the same value).
  const [mountId] = useState(() => Math.random().toString(36).slice(2, 7));
  console.log(`🧩[${mountId}] AppContent render`);

  const location = useLocation();
  const navigate = useNavigate();
  const isLoggedIn = currentData !== null;
  const showNavigation = isLoggedIn && !['/create-account', '/verify-email', '/reset-password'].includes(location.pathname);
  const isAdmin = currentData?.data.isAdmin || false;

  const [tappedOrder, setTappedOrder] = useState<TappedOrderData | null>(null);
  usePushNotifications(currentData?.data.userId, navigate, setTappedOrder);

  // Covers the initial case: a fresh login, or a cold launch that restores
  // an existing session (currentData becomes truthy once auth resolves).
  useEffect(() => {
    if (!currentData || !Capacitor.isNativePlatform()) {
      return;
    }
    clearUnseenSoldBadge('currentData-effect');
  }, [currentData]);

  // Covers every later case the effect above can't: backgrounding the app
  // (navigating out, pressing home) and coming back doesn't unmount/remount
  // React, so a currentData-keyed effect never fires again on resume -
  // without this, the badge only ever cleared on the initial login.
  // appStateChange's isActive:true fires on every resume (iOS:
  // didBecomeActiveNotification; Android: Activity.onResume), which is
  // exactly the "user is looking at the app again" moment that matters
  // here. currentData is read via a ref since this listener is registered
  // once (empty deps) rather than re-registered on every currentData change.
  //
  // A second, independent listener on the standard web visibilitychange
  // event runs alongside this one as a fallback - it doesn't depend on
  // @capacitor/app's native plugin being correctly linked at all (pure DOM
  // API), so if appStateChange turns out not to be firing reliably here,
  // this one still should. Redundant clears are harmless (Badge.clear()
  // and the server reset are both idempotent), so there's no downside to
  // running both.
  const currentDataRef = useRef(currentData);
  currentDataRef.current = currentData;
  useEffect(() => {
    if (!Capacitor.isNativePlatform()) {
      return;
    }

    const listenerPromise = CapacitorApp.addListener('appStateChange', ({ isActive }) => {
      console.log(`🔖[appStateChange] isActive=${isActive}`);
      if (isActive && currentDataRef.current) {
        clearUnseenSoldBadge('appStateChange');
      }
    });
    listenerPromise.catch((err) => {
      // UNIMPLEMENTED here means @capacitor/app's native code isn't
      // actually linked into this build yet (cap sync alone doesn't force
      // Xcode to re-resolve/relink a newly added Swift Package) - the
      // visibilitychange fallback above still covers this in the
      // meantime, but it's worth knowing which one is actually firing.
      console.warn('🔖[appStateChange] addListener failed:', err);
    });

    const onVisibilityChange = () => {
      console.log(`🔖[visibilitychange] hidden=${document.hidden}`);
      if (!document.hidden && currentDataRef.current) {
        clearUnseenSoldBadge('visibilitychange');
      }
    };
    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
      listenerPromise.then((handle) => handle.remove());
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, []);

  // Scroll to top whenever the route changes
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  const onLogoutClick = async () => {
    await handleLogout();
    navigate('/');
  };
  
  return (
    <div className="app">
      {showNavigation && (
        <>
          <DashboardHeroBanner
            firstname={currentData?.data.firstname}
            lastname={currentData?.data.lastname}
            isAdmin={isAdmin}
            onLogoutClick={onLogoutClick}
          />
          {location.pathname !== '/admin-tools' && <GlobalCollectionSelector />}
          </>
      )}
      
      <Routes>
        {/* Login route - shown at root when not logged in */}
        <Route 
          path="/" 
          element={
            !currentData ? (
              <main className="login">
                <div className="sherpas">
                  <LoginComponent onDataSend={handleOnDataSend} />
                </div>
              </main>
            ) : (
              <Navigate to="/account" replace />
            )
          } 
        />
        
        {/* Create account route */}
        <Route 
          path="/create-account" 
          element={
            <main className="login">
              <div className="sherpas">
                <CreateAccount />
              </div>
            </main>
          } 
        />
        
        {/* Email verification route */}
        <Route 
          path="/verify-email" 
          element={
            <main className="login">
              <div className="sherpas">
                <EmailVerification/>
              </div>
            </main>
          } 
        />

        {/* Reset Password route */}
        <Route 
          path="/reset-password" 
          element={
            <main className="login">
              <div className="sherpas">
                <ResetPassword onDataSend={handleOnDataSend} />
              </div>
            </main>
          } 
        />
        
        {/* Dashboard route - protected (My Sales) */}
        <Route 
          path="/dashboard" 
          element={
            currentData ? (
              <main className="dashboard" key={dashboardRef.current}>
                <ShopifySalesDashboard 
                  salesData={currentData} 
                  firebaseToken={firebaseToken}
                />
              </main>
            ) : (
              <Navigate to="/" replace />
            )
          } 
        />

       {/* My Account route - protected */}
        <Route 
        path="/account" 
        element={
            currentData ? (
            <main className="dashboard">
                <MyAccount salesData={currentData} />
            </main>
            ) : (
            <Navigate to="/" replace />
            )
        } 
        />
       
        {/* My Products route - protected */}
        <Route 
          path="/products" 
          element={
            currentData ? (
              <main className="dashboard">
                <MyProducts salesData={currentData} firebaseToken={firebaseToken} />
              </main>
            ) : (
              <Navigate to="/" replace />
            )
          } 
        />

        {/* Edit Content route - protected */}
        <Route 
          path="/edit-content" 
          element={
            currentData ? (
              <main className="dashboard">
                <EditContent />
              </main>
            ) : (
              <Navigate to="/" replace />
            )
          } 
        />

        {/* Knowledge Center route - protected */}
        <Route 
          path="/knowledge" 
          element={
            currentData ? (
              <main className="dashboard">
                <KnowledgeCenter />
              </main>
            ) : (
              <Navigate to="/" replace />
            )
          } 
        />
       
       {/* Admin Tools route - protected and admin-only */}
        <Route 
          path="/admin-tools" 
          element={
            currentData ? (
              isAdmin ? (
                <main className="dashboard">
                  <AdminTools userData={currentData.data} />
                </main>
              ) : (
                <Navigate to="/account" replace />
              )
            ) : (
              <Navigate to="/" replace />
            )
          } 
        />
      </Routes>

      <OrderSoldModal order={tappedOrder} onClose={() => setTappedOrder(null)} />
    </div>
  );
}

function App() {
  const [currentData, setCurrentData] = useState<CurrentData | null>(null);
  const [firebaseToken, setFirebaseToken] = useState<string | null>(null);
  const [authChecked, setAuthChecked] = useState(false);
  const [underConstruction, setUnderConstruction] = useState<UnderConstruction>({
    prevent_login: false,
    message: ''
  });
  const [showLoginOverride, setShowLoginOverride] = useState(false);
  const dashboardRef = useRef<number>(0);
  const forceLogoutChecked = useRef<boolean>(false);
  const currentDataRef = useRef<CurrentData | null>(null);
  const isLoggingIn = useRef<boolean>(false);
  const userRoleRef = useRef<string | null>(null);

  // Update ref when currentData changes
  useEffect(() => {
    currentDataRef.current = currentData;
    if (currentData) {
      userRoleRef.current = currentData.data.isAdmin ? 'admin' : 'user';
    } else {
      userRoleRef.current = null;
    }
  }, [currentData]);

  // Native splash screen stays up (launchAutoHide: false in
  // capacitor.config.ts) past its default instant dismissal - but it's a
  // native overlay that sits ON TOP of the WebView, so it has to come
  // down as soon as the web app has painted anything at all, not once
  // auth has resolved. The branded logo+"Loading..." screen below is
  // rendered as a normal web <img> specifically so it's visible the
  // instant this effect fires (empty deps = runs right after first
  // paint), independent of whatever the native splash itself managed to
  // show during the brief native-only window beforehand.
  useEffect(() => {
    if (Capacitor.isNativePlatform()) {
      SplashScreen.hide().catch((err) => {
        console.warn('SplashScreen.hide() failed:', err);
      });
    }
  }, []);

  // Listen to system config for construction mode and force logout
 // Listen to system config for construction mode and force logout
useEffect(() => {
  // Listen to under_construction document
  const underConstructionRef = doc(db, 'system_config', 'under_construction');
  const unsubscribeConstruction = onSnapshot(underConstructionRef, (doc) => {
    console.log('🚧 Under construction snapshot received:', doc.exists());
    
    if (doc.exists()) {
      const data = doc.data() as UnderConstruction;
      console.log('🚧 Under construction data:', data);
      
      setUnderConstruction({
        prevent_login: data.prevent_login || false,
        message: data.message || "We are currently making some updates, but will be back online soon."
      });

      // IMPORTANT: Reset override flag when construction mode is disabled
      if (!data.prevent_login) {
        setShowLoginOverride(false);
      }
    } else {
      // Document doesn't exist, set to defaults
      setUnderConstruction({
        prevent_login: false,
        message: "We are currently making some updates, but will be back online soon."
      });
      // Reset override when no document exists
      setShowLoginOverride(false);
    }
  });

  // ... rest of the useEffect

    // Listen to config document for force logout
    const configRef = doc(db, 'system_config', 'config');
    const unsubscribeConfig = onSnapshot(configRef, (doc) => {
      if (doc.exists()) {
        const data = doc.data() as SystemConfig;
        
        // Check force logout
        const forceLogoutTimestamp = data.force_logout_timestamp || 0;
        const lastLogin = parseInt(localStorage.getItem('last_login') || '0');
        
        if (lastLogin < forceLogoutTimestamp && auth.currentUser) {
          signOut(auth);
          localStorage.removeItem('last_login');
          alert('Please log back in due to a security update.');
        }
      }
    });

    return () => {
      unsubscribeConstruction();
      unsubscribeConfig();
    };
  }, []);

  // Restore authentication on page load/refresh
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user && user.emailVerified) {
        try {
          console.log('Restoring authentication for user:', user.uid);
          
          // Skip if we're actively logging in (LoginComponent will handle it)
          if (isLoggingIn.current) {
            console.log('⏭️ Skipping auth restore - login in progress');
            setAuthChecked(true);
            return;
          }
          
          // Skip if we already have currentData (user already logged in this session)
          if (currentDataRef.current) {
            console.log('⏭️ Skipping auth restore - already have currentData');
            setAuthChecked(true);
            return;
          }
          
          // Get fresh ID token with custom claims
          const idTokenResult = await user.getIdTokenResult();
          const userDocRef = doc(db, 'users', user.uid);
          const userDoc = await getDoc(userDocRef);
          const userData = userDoc.data();
          
          // IMPORTANT: Get isAdmin from custom claims (more secure)
          const isAdmin = idTokenResult.claims.isAdmin || false;
          
          // CHECK FOR FORCED LOGOUT - only on page refresh, not after login
          // This check only runs when restoring a session (page refresh)
          if (!forceLogoutChecked.current) {
            forceLogoutChecked.current = true;
            
            const forceLogoutRef = doc(db, 'system_config', 'force_logout');
            const forceLogoutDoc = await getDoc(forceLogoutRef);
            
            if (forceLogoutDoc.exists()) {
              const forceLogoutData = forceLogoutDoc.data();
              const forceLogoutTime = forceLogoutData?.lastForceLogoutTime?.toDate();
              
              if (forceLogoutTime) {
                console.log('Force logout time exists:', forceLogoutTime);
                
                let userLastSignIn: Date | null = null;
                
                if (userData?.lastSignInAt) {
                  if (userData.lastSignInAt.toDate) {
                    userLastSignIn = userData.lastSignInAt.toDate();
                  } else if (userData.lastSignInAt.seconds) {
                    userLastSignIn = new Date(userData.lastSignInAt.seconds * 1000);
                  }
                }
                
                console.log('User last sign-in:', userLastSignIn);
                
                // If user logged in BEFORE the force logout time, sign them out
                if (userLastSignIn && userLastSignIn < forceLogoutTime) {
                  console.log('User login is older than force logout time - signing out');
                  await signOut(auth);
                
                  // Get the reason from Firestore data
                  const reason = forceLogoutData?.reason || 'a security update';
                  alert(`Please log back in.${reason}.`);
                
                  setAuthChecked(true);
                  return;
                }
              }
            }
          }
          
          const initialData: CurrentData = {
            success: true,
            data: {
              userId: user.uid,
              email: user.email || '',
              firstname: userData?.firstname || '',
              lastname: userData?.lastname || '',
              isAdmin: isAdmin,
              tags: userData?.collectionHandles || [],
              salesDataByTag: [],
              tagsArray: userData?.collectionHandles || []
            }
          };

          console.log(`🔐 setCurrentData from auth-restore, userId=${initialData.data.userId}`);
          setCurrentData(initialData);
          setFirebaseToken(idTokenResult.token);
          console.log('Authentication restored successfully');
          console.log('Admin status from custom claim:', isAdmin);
    
        } catch (error) {
          console.error('Error restoring auth:', error);
        }
      }
      setAuthChecked(true);
    });

    return () => unsubscribe();
  }, []);

  const handleOnDataSend = (data: CurrentData, token: string) => {
    if (data?.data?.isAdmin) {
      console.log('Admin user detected');
    }
    isLoggingIn.current = true;
    
    // Mark force logout as checked since fresh login = fresh session
    forceLogoutChecked.current = true;
    
    // Create new object to force re-render
    console.log(`🔐 setCurrentData from handleOnDataSend, userId=${data?.data?.userId}`);
    setCurrentData({...data});
    setFirebaseToken(token);
    dashboardRef.current += 1;

    // Clear login flag after a delay
    setTimeout(() => {
      isLoggingIn.current = false;
    }, 1000);
  };

  const handleLogout = async () => {
    try {
      await signOut(auth);
      setCurrentData(null);
      setFirebaseToken(null);
      dashboardRef.current = 0;
      currentDataRef.current = null;
      userRoleRef.current = null;
      setShowLoginOverride(false);
      // NOTE: We do NOT reset forceLogoutChecked here!
      // This prevents the race condition where onAuthStateChanged
      // checks force logout before lastSignInAt is updated.
      // forceLogoutChecked only resets on full page refresh.
      console.log('User logged out successfully');
    } catch (error) {
      console.error('Error logging out:', error);
    }
  };

  // Don't render routes until auth check is complete. Shown as a web
  // <img>, not dependent on the native splash screen's own rendering of
  // the same logo - @capacitor/splash-screen has a long-standing,
  // unresolved iOS bug (ionic-team/capacitor#3589) where the native
  // splash's re-instantiated storyboard sometimes fails to load its image
  // at runtime, leaving just a flat background color. Rendering the same
  // branded logo here means the user still sees it almost immediately via
  // the web app itself, regardless of whether that native bug ever gets
  // fixed - the gap is however long it takes this screen to paint, not
  // the full authChecked round trip.
  if (!authChecked) {
    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        gap: '20px',
        height: '100vh',
        background: '#17212B'
      }}>
        <img src={splashLogo} alt="Merch Sherpa" style={{ width: '160px', height: 'auto' }} />
        <span style={{ color: 'rgba(255,255,255,0.65)', fontSize: '14px' }}>Loading...</span>
      </div>
    );
  }

  console.log('🚧 Construction check:', {
    prevent_login: underConstruction.prevent_login,
    userRole: userRoleRef.current,
    hasCurrentData: !!currentData,
    isAdmin: currentData?.data.isAdmin,
    showLoginOverride: showLoginOverride,
    shouldBlock: underConstruction.prevent_login && currentData && userRoleRef.current === 'user'
  });

  // CONSTRUCTION MODE LOGIC
  if (underConstruction.prevent_login) {
    // Case 1: User is logged in as non-admin - sign them out and show construction
    if (currentData && userRoleRef.current === 'user') {
      console.log('🚧 BLOCKING NON-ADMIN USER - showing construction screen');
      signOut(auth);
      setCurrentData(null);
      setFirebaseToken(null);
      return <ConstructionScreen onSecretKnock={() => setShowLoginOverride(true)} />;
    }
    
    // Case 2: User is logged in as admin - let them through
    if (currentData && userRoleRef.current === 'admin') {
      console.log('✅ Admin user - allowing access');
      // Continue to render app below
    }
    
    // Case 3: No one is logged in, but secret knock NOT activated - show construction
    if (!currentData && !showLoginOverride) {
      console.log('🚧 Construction mode active - showing construction screen');
      return <ConstructionScreen onSecretKnock={() => setShowLoginOverride(true)} />;
    }
    
    // Case 4: No one is logged in, but secret knock IS activated - show login
    if (!currentData && showLoginOverride) {
      console.log('🔓 Secret knock activated - showing login screen');
      // Continue to render app (which will show login) below
    }
  }

  console.log('✅ Passed construction check - rendering app');

  return (
    <BrowserRouter>
      <ProductsCacheProvider>
        <SalesDataProvider>
          <CollectionProvider currentData={currentData}>
            <AppContent 
              currentData={currentData}
              firebaseToken={firebaseToken}
              handleOnDataSend={handleOnDataSend}
              handleLogout={handleLogout}
              dashboardRef={dashboardRef}
            />
          </CollectionProvider>
        </SalesDataProvider>
      </ProductsCacheProvider>
    </BrowserRouter>
  );
}

export default App;